import React from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';

import { CORRIDOR_TYPES } from '@/entities/dashboard/mapping';

import type { MapProvider, MapProviderProps } from '../../model/map-provider.interface';

import { calculateMapCenter, MAP_DEFAULTS } from './lib/map-calculations';
import { ClusterPieLayer } from './ui/cluster-pie-layer';
import { LocationIconLayer } from './ui/location-icon-layer';
import { CorridorLayer } from './ui/corridor-layer';
import { MapViewManager } from './ui/map-view-manager';

import 'leaflet/dist/leaflet.css';
import './map-styles.css';

const MapContent = React.memo(function MapContent({
  clusters,
  iconLocations,
  corridors,
  fitPoints,
  dataFilters,
  dimensionLabel,
  weeksAgo
}: MapProviderProps) {
  const maxTotal = clusters.reduce((max, cluster) => Math.max(max, cluster.total), 0);

  return (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <MapViewManager points={fitPoints} />

      <CorridorLayer corridors={corridors[CORRIDOR_TYPES.IN_HOUSE]} />
      <CorridorLayer corridors={corridors[CORRIDOR_TYPES.IN_TRANSIT]} />

      <LocationIconLayer locations={iconLocations} />

      {clusters.map((cluster) => (
        <ClusterPieLayer
          key={cluster.id}
          cluster={cluster}
          maxTotal={maxTotal}
          weeksAgo={weeksAgo}
          dimensionLabel={dimensionLabel}
          dataFilters={dataFilters}
        />
      ))}
    </>
  );
});

export class OpenStreetMapProvider implements MapProvider {
  private mapKey = 'osm-map-instance';

  render(props: MapProviderProps): React.ReactElement {
    const { clusters, config, style, className } = props;

    return (
      <MapContainer
        key={this.mapKey}
        center={calculateMapCenter(clusters, config?.center)}
        zoom={config?.zoom ?? MAP_DEFAULTS.ZOOM}
        style={style || { height: '100%', width: '100%' }}
        className={className || 'rounded-lg'}
        scrollWheelZoom={true}
        zoomControl={true}>
        <MapContent {...props} />
      </MapContainer>
    );
  }

  destroy(): void {
    // Cleanup if needed
  }
}
